"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  animate,
  AnimationPlaybackControls,
} from "framer-motion";
import { cn } from "@/lib/utils";

interface MarqueeProps {
  children: React.ReactNode;
  className?: string;
  velocity?: number; // Positive = Right, Negative = Left
  pauseOnHover?: boolean;
}

export function Marquee({
  children,
  className,
  velocity = 0.5, // Reasonable default speed factor
  pauseOnHover = false,
}: MarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const x = useMotionValue(0);
  const controlsRef = useRef<AnimationPlaybackControls | null>(null);

  // Measure measurement-wrapper width
  useEffect(() => {
    const updateWidth = () => {
      if (contentRef.current) {
        setContentWidth(contentRef.current.offsetWidth);
      }
    };

    // Initial measure
    updateWidth();

    // Observe changes
    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [children]);

  // Setup Animation
  useEffect(() => {
    if (contentWidth === 0) return;

    const isMovingLeft = velocity < 0;
    // Speed factor: velocity 1 ~= 100px/s approximately?
    // Let's tune multiplier to match previous "feel".
    // Previous: 0.3 was slow.
    // If we want 0.3 to comply with requested "Slow", let's say 100 is base.
    const speed = Math.abs(velocity) * 100;
    const duration = contentWidth / speed;

    // Logic:
    // If moving LEFT (neg): We animate from 0 to -contentWidth.
    // At -contentWidth, the first set has fully scrolled out, and the second set is exactly where the first one started.
    // Determine From/To
    const from = isMovingLeft ? 0 : -contentWidth;
    const to = isMovingLeft ? -contentWidth : 0;

    // Stop existing
    if (controlsRef.current) controlsRef.current.stop();

    // Start new
    controlsRef.current = animate(x, to, {
      from,
      duration,
      ease: "linear",
      repeat: Infinity,
      repeatType: "loop",
    });

    return () => {
      if (controlsRef.current) controlsRef.current.stop();
    };
  }, [contentWidth, velocity, x]);

  const handleMouseEnter = () => {
    if (pauseOnHover && controlsRef.current) {
      controlsRef.current.pause();
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover && controlsRef.current) {
      controlsRef.current.play();
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("w-full overflow-hidden", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div className="flex w-max" style={{ x }}>
        {/* Render 4 copies for safety on wide screens */}
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            ref={i === 0 ? contentRef : null}
            className="flex shrink-0 gap-(--gap) pr-(--gap)"
          >
            {children}
          </div>
        ))}
      </motion.div>
    </div>
  );
}
